import { Order } from '../types';

const GHOST_VIRTUAL_EXTERNAL_STATUSES = new Set<Order['status']>([
    'dispatched',
    'dispatched_3rd_party',
    'delivered',
    'archived'
]);

/**
 * Verifica se um pedido é elegível para Ghost Dispatch (máscara de status).
 * 
 * CRITICAL (v44.7): Apenas pedidos de FROTA PRÓPRIA são elegíveis.
 * Pedidos marcados para envio externo (third-party) ou ainda não processados (undefined)
 * devem ser EXCLUÍDOS para evitar envio prematuro de "saiu para entrega" ao iFood.
 */
export const isGhostDispatchEligibleForMasking = (
    order: Pick<Order, 'ghostDispatch' | 'pickupConfirmed' | 'type' | 'salesChannel' | 'optimizationOutcome' | 'manualAssignment' | 'bestProvider' | 'externalDeliveryId'>
): boolean => {
    // Verificações básicas de elegibilidade
    const basicEligibility = Boolean(
        order.ghostDispatch &&
        !order.pickupConfirmed &&
        order.type === 'DELIVERY' &&
        order.salesChannel?.toLowerCase().includes('ifood')
    );

    if (!basicEligibility) return false;

    // CRITICAL v44.7: Apenas frota própria explicitamente confirmada.
    // Pedidos com campos undefined (ainda não processados pelo motor de roteamento)
    // NÃO são elegíveis até que seja tomada uma decisão explícita de frota própria.
    const isExplicitlyOwnFleet = 
        order.optimizationOutcome === 'own' ||
        order.manualAssignment === 'own';

    // Também excluir explicitamente se houver qualquer indicação de envio externo
    const isExplicitlyExternal = 
        order.optimizationOutcome === 'third' ||
        order.manualAssignment === 'third' ||
        Boolean(order.bestProvider) ||
        Boolean(order.externalDeliveryId);

    // Só elegível se for EXPLICITAMENTE frota própria e NÃO for externo
    return isExplicitlyOwnFleet && !isExplicitlyExternal;
};

export const shouldMaskGhostDispatchExternalStatus = (
    order: Pick<Order, 'ghostDispatch' | 'pickupConfirmed' | 'type' | 'salesChannel' | 'optimizationOutcome' | 'manualAssignment' | 'bestProvider' | 'externalDeliveryId'>,
    incomingStatus: Order['status']
): boolean => {
    return (
        isGhostDispatchEligibleForMasking(order) &&
        GHOST_VIRTUAL_EXTERNAL_STATUSES.has(incomingStatus)
    );
};
